import { type ReactNode } from "react";
import { Loader2, Sparkles } from "lucide-react";
import type { CatalogComparisonSummaryResponse } from "../../../../api/frameworksApi";

export function AiSummaryCard({
  loading,
  error,
  data,
}: {
  loading: boolean;
  error: string | null;
  data: CatalogComparisonSummaryResponse | string | null;
}) {
  if (!loading && !error && !data) return null;

  const narrative =
    typeof data === "string"
      ? data
      : (data?.summary ?? data?.narrative ?? data?.text ?? "");

  const lists: { label: string; items: string[] }[] = [];
  if (typeof data === "object" && data) {
    const push = (label: string, value: unknown) => {
      if (Array.isArray(value) && value.length) {
        lists.push({ label, items: value.map((v) => String(v)) });
      }
    };
    push("Key Findings", data.keyFindings);
    push("Insights", data.insights);
    push("Recommendations", data.recommendations);
  }

  const blocks: { type: "p" | "ul"; items: string[] }[] = [];
  String(narrative ?? "")
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean)
    .forEach((line) => {
      const isBullet = /^[-*•]\s+/.test(line);
      const text = isBullet ? line.replace(/^[-*•]\s+/, "") : line;
      const last = blocks[blocks.length - 1];
      if (isBullet && last?.type === "ul") last.items.push(text);
      else blocks.push({ type: isBullet ? "ul" : "p", items: [text] });
    });

  const hasContent = blocks.length > 0 || lists.length > 0;

  return (
    <section className="rounded-xl border border-indigo-100 dark:border-indigo-400/20 bg-indigo-50/50 dark:bg-indigo-500/[0.06] p-4 space-y-3">
      <div className="flex items-center gap-2">
        <span className="flex-shrink-0 size-6 rounded-md bg-indigo-100 dark:bg-indigo-500/15 flex items-center justify-center">
          <Sparkles className="size-3.5 text-indigo-600 dark:text-indigo-400" />
        </span>
        <h3 className="text-[13px] font-bold text-[#0f172a] dark:text-white">
          AI Summary
        </h3>
      </div>

      {loading ? (
        <p className="inline-flex items-center gap-2 text-[12px] text-[#475569] dark:text-white/60">
          <Loader2 className="size-3.5 animate-spin text-indigo-500" />
          AI data is generating
        </p>
      ) : error ? (
        <p className="text-[12px] text-red-700 dark:text-red-300 break-words">
          {error}
        </p>
      ) : hasContent ? (
        <div className="space-y-3">
          {blocks.map((block, i) =>
            block.type === "ul" ? (
              <ul key={i} className="space-y-2">
                {block.items.map((item, j) => (
                  <li
                    key={j}
                    className="flex items-start gap-2 text-[12px] leading-relaxed text-[#475569] dark:text-white/70"
                  >
                    <span className="mt-1.5 size-1.5 flex-shrink-0 rounded-full bg-indigo-500" />
                    <span className="break-words">{item}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p
                key={i}
                className="text-[12px] leading-relaxed text-[#475569] dark:text-white/70 break-words"
              >
                {block.items[0]}
              </p>
            ),
          )}
          {lists.map((list) => (
            <div key={list.label} className="space-y-1.5">
              <p className="text-[11px] font-bold text-[#0f172a] dark:text-white">
                {list.label}
              </p>
              <ul className="space-y-1">
                {list.items.map((item, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2 text-[12px] leading-relaxed text-[#475569] dark:text-white/70"
                  >
                    <span className="mt-1.5 size-1.5 flex-shrink-0 rounded-full bg-indigo-500" />
                    <span className="break-words">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      ) : (
        <pre className="text-[11px] leading-relaxed text-[#475569] dark:text-white/70 whitespace-pre-wrap break-words">
          {JSON.stringify(data, null, 2)}
        </pre>
      )}
    </section>
  );
}


const SECTION_TONE = {
  emerald: {
    border: "border-emerald-100 dark:border-emerald-400/20",
    surface: "bg-emerald-50/50 dark:bg-emerald-500/[0.05]",
    chip: "bg-emerald-100 dark:bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
    value: "text-emerald-600 dark:text-emerald-400",
  },
  amber: {
    border: "border-amber-100 dark:border-amber-400/20",
    surface: "bg-amber-50/50 dark:bg-amber-500/[0.05]",
    chip: "bg-amber-100 dark:bg-amber-500/15 text-amber-600 dark:text-amber-400",
    value: "text-amber-600 dark:text-amber-400",
  },
  violet: {
    border: "border-violet-100 dark:border-violet-400/20",
    surface: "bg-violet-50/50 dark:bg-violet-500/[0.05]",
    chip: "bg-violet-100 dark:bg-violet-500/15 text-violet-600 dark:text-violet-400",
    value: "text-violet-600 dark:text-violet-400",
  },
} as const;

/** One outcome: what it means, how big it is, and the controls behind it. */
export function OutcomeSection({
  tone,
  icon,
  title,
  count,
  share,
  description,
  open,
  onToggle,
  empty,
  filters,
  children,
}: {
  tone: keyof typeof SECTION_TONE;
  icon: ReactNode;
  title: string;
  count: number;
  share: number;
  description: string;
  open: boolean;
  onToggle: () => void;
  empty?: boolean;
  filters?: ReactNode;
  children: ReactNode;
}) {
  const t = SECTION_TONE[tone];
  return (
    <section
      className={`rounded-xl border ${t.border} ${t.surface} overflow-hidden`}
    >
      <div className="p-4 space-y-2">
        <div className="flex items-start gap-3">
          <span
            className={`flex-shrink-0 size-7 rounded-lg flex items-center justify-center ${t.chip}`}
          >
            {icon}
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-bold text-[#0f172a] dark:text-white break-words">
              {title}
            </p>
            <p className="mt-0.5 text-[11px] leading-relaxed text-[#64748b] dark:text-white/55 break-words">
              {description}
            </p>
          </div>
          <div className="flex-shrink-0 text-right">
            <p className={`text-xl font-bold leading-none ${t.value}`}>
              {count}
            </p>
            <p className="mt-1 text-[10px] font-semibold text-[#64748b] dark:text-white/50">
              {`${share}% of target`}
            </p>
          </div>
        </div>
        {filters}
        <button
          type="button"
          onClick={onToggle}
          disabled={empty}
          className="text-[11px] font-semibold text-indigo-600 dark:text-indigo-400 hover:underline disabled:opacity-45 disabled:hover:no-underline disabled:cursor-not-allowed"
        >
          {empty
            ? "No controls to show"
            : open
              ? "Hide controls"
              : "View controls"}
        </button>
      </div>
      {open && !empty && (
        <div className="border-t border-[#e2e8f0] dark:border-white/[0.06] bg-white dark:bg-white/[0.02] p-3">
          {/* The table header is square, so it is clipped to this rounded
              frame rather than sitting with bare corners inside the card. */}
          <div className="overflow-hidden rounded-lg border border-[#e2e8f0] dark:border-white/[0.06]">
            {children}
          </div>
        </div>
      )}
    </section>
  );
}

/** Key under the readiness bar: colour, what it means, how many. */
export function BarKey({
  color,
  label,
  value,
}: {
  color: string;
  label: string;
  value: number;
}) {
  return (
    <span className="flex w-full items-center gap-2 text-[11px] text-[#475569] dark:text-white/65">
      <span
        className="size-2 flex-shrink-0 rounded-full"
        style={{ backgroundColor: color }}
      />
      <span className="min-w-0 flex-1 break-words">{label}</span>
      <span className="flex-shrink-0 font-bold text-[#0f172a] dark:text-white">
        {value}
      </span>
    </span>
  );
}

/** Narrows the in-progress table to one source implementation status. */
export function StatusChip({
  active,
  label,
  count,
  color,
  onClick,
}: {
  active: boolean;
  label: string;
  count: number;
  color?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors ${
        active
          ? "border-indigo-500 bg-indigo-500/10 text-indigo-600 dark:text-indigo-300"
          : "border-[#e2e8f0] dark:border-white/[0.08] text-[#475569] dark:text-white/60 hover:border-indigo-400/60"
      }`}
    >
      {color && (
        <span
          className="size-2 flex-shrink-0 rounded-full"
          style={{ backgroundColor: color }}
        />
      )}
      <span className="break-words">{label}</span>
      <span className="text-[#0f172a] dark:text-white">{count}</span>
    </button>
  );
}

/** Scope context, deliberately quieter than the outcome sections. */
export function ScopeStat({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value: number;
}) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] text-[#64748b] dark:text-white/50">
      <span className="text-[#94a3b8] dark:text-white/40">{icon}</span>
      <span className="break-words">{label}</span>
      <span className="font-bold text-[#0f172a] dark:text-white">{value}</span>
    </span>
  );
}

/* Score bands, kept only for the colour the number is drawn in - the rating
   label itself is deliberately not shown. */
const SCORE_COLORS: { min: number; color: string }[] = [
  { min: 90, color: "#10b981" },
  { min: 70, color: "#f59e0b" },
  { min: 50, color: "#f97316" },
  { min: 0, color: "#ef4444" },
];


export function TargetScoreCard({
  score,
  catalogName,
  subtitle,
}: {
  score: number;
  catalogName: string;
  subtitle?: string;
}) {
  const band =
    SCORE_COLORS.find((b) => score >= b.min) ??
    SCORE_COLORS[SCORE_COLORS.length - 1];
  const color = band.color;

  return (
    <div
      className="relative rounded-lg border px-4 py-3 overflow-hidden flex items-center gap-3 flex-wrap sm:flex-nowrap"
      style={{
        background: `linear-gradient(135deg, ${color}0F 0%, transparent 100%)`,
        borderColor: `${color}33`,
      }}
    >
      <div
        className="absolute top-0 right-0 size-20 rounded-full opacity-20 blur-xl pointer-events-none"
        style={{ background: color }}
      />

      <div className="relative flex-shrink-0 flex flex-col items-center text-center">
        <p
          className="font-bold tabular-nums leading-none tracking-tight"
          style={{ color, fontSize: 44 }}
        >
          {score}
          <span className="text-[20px] font-bold ml-0.5">%</span>
        </p>
        <p className="mt-1.5 text-[9px] font-semibold tracking-wider text-[#94a3b8] dark:text-white/45 whitespace-nowrap">
          Compliance Score
        </p>
      </div>

      <div className="relative flex-1 min-w-0">
        <p className="text-[15px] font-bold tracking-tight text-[#0f172a] dark:text-white break-words min-w-0">
          {catalogName}
        </p>
        {subtitle && (
          <p className="mt-1 text-[11px] text-[#64748b] dark:text-white/55 break-words">
            {subtitle}
          </p>
        )}
      </div>
    </div>
  );
}
